/**
 * Error Types and Enums
 *
 * Defines error severity levels, categories, and the AppError class
 * for structured error handling throughout the application.
 */

/**
 * Error severity levels
 *
 * Indicates the severity of an error for logging and monitoring purposes.
 */
export enum ErrorSeverity {
    /** Low severity - informational errors that don't impact functionality */
    LOW = 'low',

    /** Medium severity - errors that impact some functionality but have workarounds */
    MEDIUM = 'medium',

    /** High severity - errors that significantly impact functionality */
    HIGH = 'high',

    /** Critical severity - errors that prevent core functionality or require immediate attention */
    CRITICAL = 'critical',
}

/**
 * Error categories
 *
 * Categorizes errors by their source or type for better error handling and routing.
 */
export enum ErrorCategory {
    /** Network-related errors (connection issues, timeouts, etc.) */
    NETWORK = 'network',

    /** Validation errors (invalid input, failed validation rules) */
    VALIDATION = 'validation',

    /** Authentication errors (login failures, invalid credentials) */
    AUTHENTICATION = 'authentication',

    /** Authorization errors (insufficient permissions, access denied) */
    AUTHORIZATION = 'authorization',

    /** Server errors (5xx responses, backend failures) */
    SERVER = 'server',

    /** Client errors (4xx responses, invalid requests) */
    CLIENT = 'client',

    /** Unknown or uncategorized errors */
    UNKNOWN = 'unknown',
}

/**
 * Error context information
 *
 * Additional contextual information about where and when an error occurred.
 */
export interface ErrorContext {
    /** User ID if authenticated */
    userId?: string

    /** Current route or page */
    route?: string

    /** Action being performed when error occurred */
    action?: string

    /** Additional metadata about the error */
    metadata?: Record<string, unknown>

    /** Timestamp when error occurred */
    timestamp?: number

    /** Redux state snapshot (sanitized) */
    stateSnapshot?: Record<string, unknown>

    /** Recent user actions */
    recentActions?: Array<{
        type: string
        timestamp: number
        route?: string
        metadata?: Record<string, unknown>
    }>
}

/**
 * AppError class
 *
 * Extended Error class with additional context and categorization
 * for better error handling and monitoring.
 */
export class AppError extends Error {
    /** Error code for programmatic identification */
    public readonly code: string

    /** Severity level of the error */
    public readonly severity: ErrorSeverity

    /** Category of the error */
    public readonly category: ErrorCategory

    /** Additional context about the error */
    public readonly context?: ErrorContext

    /** Whether the error is recoverable */
    public readonly recoverable: boolean

    /** User-friendly error message */
    public readonly userMessage: string

    /** Original error if this wraps another error */
    public readonly originalError?: Error

    constructor(
        message: string,
        options: {
            code: string
            severity: ErrorSeverity
            category: ErrorCategory
            context?: ErrorContext
            recoverable?: boolean
            userMessage?: string
            originalError?: Error
        }
    ) {
        super(message)

        // Maintains proper stack trace for where our error was thrown (only available on V8)
        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, AppError)
        }

        this.name = 'AppError'
        this.code = options.code
        this.severity = options.severity
        this.category = options.category
        this.context = options.context
        this.recoverable = options.recoverable ?? false
        this.userMessage =
            options.userMessage ??
            'An unexpected error occurred. Please try again.'
        this.originalError = options.originalError

        // Add timestamp to context if not provided
        if (this.context && !this.context.timestamp) {
            this.context.timestamp = Date.now()
        } else if (!this.context) {
            this.context = { timestamp: Date.now() }
        }
    }

    /**
     * Converts the error to a JSON-serializable object
     */
    toJSON(): Record<string, unknown> {
        return {
            name: this.name,
            message: this.message,
            code: this.code,
            severity: this.severity,
            category: this.category,
            context: this.context,
            recoverable: this.recoverable,
            userMessage: this.userMessage,
            stack: this.stack,
            originalError: this.originalError
                ? {
                      name: this.originalError.name,
                      message: this.originalError.message,
                      stack: this.originalError.stack,
                  }
                : undefined,
        }
    }
}
